import MoreVert from "@mui/icons-material/MoreVert";
import OpenInNew from "@mui/icons-material/OpenInNew";
import {
  Checkbox,
  IconButton,
  Link,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import { useState } from "react";
import { Link as RouterLink } from "react-router-dom";

import { StepAction } from "./StepAction";

/**
 * The manifest, under the step that receives it.
 *
 * It used to be a card of its own beside Shipment Details, which repeated what
 * the step already said — the rail has always printed the tracking number and
 * the received date — and left the page carrying the same fact twice. The
 * manifest is a stage of the shipment's life, so it says so where the other
 * stages do.
 *
 * Its three actions stay behind one menu rather than becoming a row of icons.
 * The rail is 240–290px wide and deliberately quiet: one control beside the
 * number reads as an aside, where three competing glyphs would make the
 * narrowest column on the page also the busiest.
 */
export function ManifestStepActions({
  trackingNumber,
  manifestHref,
  signed,
  /** True while the link is attached but its record is still rebuilding. */
  dangling,
  attachBlockedReason,
  signedChangePending,
  onAttach,
  onRelink,
  onDetach,
  onSignedChange,
}: {
  /** Null when nothing is attached, which is what selects the empty state. */
  trackingNumber: string | null;
  /** The manifest's own page, or null while the link dangles. */
  manifestHref: string | null;
  signed: boolean;
  dangling: boolean;
  /** Why attaching is unavailable right now, or undefined when it is not. */
  attachBlockedReason?: string;
  /**
   * True while a signed-copy change is still in flight.
   *
   * Marking a copy signed re-attaches the manifest already linked, so it is the
   * attach mutation under another name — and toggling again before the first
   * lands sends two of them with opposite values, leaving the setting wherever
   * the server happens to finish last.
   */
  signedChangePending?: boolean;
  onAttach: () => void;
  onRelink: () => void;
  onDetach: () => void;
  onSignedChange: (signed: boolean) => void;
}) {
  if (!trackingNumber) {
    return (
      <StepAction label="Attach manifest" blocked={attachBlockedReason ?? ""} onClick={onAttach} />
    );
  }

  return (
    <Stack direction="row" spacing={0.5} alignItems="center" sx={{ minWidth: 0 }}>
      {/* The number is the only thing here that gives up room, so in a 240px
          rail it is also the only thing that can end in an ellipsis — the
          tooltip is how the whole of it stays readable. */}
      <Tooltip title={trackingNumber}>
        {manifestHref ? (
          <Link
            component={RouterLink}
            to={manifestHref}
            // A new tab, as the icon beside it promises. The manifest is read
            // against the shipment, so following it must not cost the reader
            // the page they were comparing it to.
            target="_blank"
            rel="noopener noreferrer"
            variant="body2"
            sx={{ display: "inline-flex", alignItems: "center", gap: 0.5, minWidth: 0 }}
          >
            <Typography variant="body2" component="span" noWrap sx={{ minWidth: 0 }}>
              {trackingNumber}
            </Typography>
            <OpenInNew sx={{ fontSize: 14, flexShrink: 0 }} />
          </Link>
        ) : (
          <Typography variant="body2" noWrap sx={{ minWidth: 0 }}>
            {trackingNumber}
          </Typography>
        )}
      </Tooltip>
      <ManifestActionsMenu
        signed={signed}
        // Nothing to act on while the record is rebuilding: all three act on a
        // manifest row that is not there yet.
        disabled={dangling}
        disabledReason={
          dangling ? "Available once the manifest record has finished rebuilding." : undefined
        }
        // Replacing attaches, so it locks the contents the same way a first
        // attach does — and is blocked for the same reason.
        replaceBlockedReason={attachBlockedReason}
        signedChangePending={signedChangePending}
        onSignedChange={onSignedChange}
        onRelink={onRelink}
        onDetach={onDetach}
      />
    </Stack>
  );
}

/**
 * The manifest's three actions, behind one control.
 *
 * Signing is a checkbox item rather than a switch because it sits in the same
 * list as the two that are not toggles, and a menu of mixed shapes reads worse
 * than one that states its own state. It is also the only one of the three that
 * is reversible, which a checkbox says and a plain item would not.
 */
function ManifestActionsMenu({
  signed,
  disabled,
  disabledReason,
  replaceBlockedReason,
  signedChangePending,
  onSignedChange,
  onRelink,
  onDetach,
}: {
  signed: boolean;
  disabled: boolean;
  /** Why the actions are unavailable, when they are. */
  disabledReason?: string;
  /** Why replacing specifically is unavailable — it attaches, so it locks. */
  replaceBlockedReason?: string;
  /** True while a signed-copy change is still in flight; see the caller. */
  signedChangePending?: boolean;
  onSignedChange: (signed: boolean) => void;
  onRelink: () => void;
  onDetach: () => void;
}) {
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const close = () => setAnchor(null);
  const run = (action: () => void) => () => {
    close();
    action();
  };

  return (
    <>
      <Tooltip title={disabledReason ?? "Manifest actions"}>
        <span>
          <IconButton
            size="small"
            aria-label="Manifest actions"
            disabled={disabled}
            onClick={(e) => setAnchor(e.currentTarget)}
          >
            <MoreVert fontSize="small" />
          </IconButton>
        </span>
      </Tooltip>
      <Menu anchorEl={anchor} open={Boolean(anchor)} onClose={close}>
        {/*
          Held while its own change is still in flight. This item re-attaches the
          manifest to record the new value, so toggling it again before the first
          lands puts two of the same mutation in the air with opposite values and
          the setting ends up wherever the server finishes last. Nothing explains
          the pause because it lasts as long as the request does.
        */}
        <MenuItem disabled={signedChangePending} onClick={run(() => onSignedChange(!signed))}>
          <ListItemIcon>
            <Checkbox size="small" edge="start" checked={signed} tabIndex={-1} disableRipple />
          </ListItemIcon>
          <ListItemText primary="Mark as signed copy" />
        </MenuItem>
        {/*
          Wrapped only while it is unavailable, which is the one state where the
          wrapper earns its keep.

          A span around a disabled control is the standard way to make a tooltip
          work at all, since a disabled child drops the pointer events the tooltip
          listens for. But `MenuList` moves focus across its own direct children,
          so a permanent wrapper left this item unreachable by arrow keys even
          when it was enabled and was the thing the user came for. Disabled items
          are skipped anyway, so wrapping only then costs nothing.

          The reason belongs in the tooltip rather than in the item. Inside it, as
          the label it displaced what the item does, and as secondary text it
          inherited the item's disabled opacity on top of its own — landing near
          1.6:1 against the menu, which is not readable. Either way `MenuItem` does
          not wrap text, so a sentence-long reason stretched the popover.
        */}
        {replaceBlockedReason ? (
          <Tooltip title={replaceBlockedReason}>
            <span>
              <MenuItem disabled>
                <ListItemText primary="Replace this manifest" />
              </MenuItem>
            </span>
          </Tooltip>
        ) : (
          <MenuItem onClick={run(onRelink)}>
            <ListItemText primary="Replace this manifest" />
          </MenuItem>
        )}
        <MenuItem onClick={run(onDetach)}>Detach this manifest</MenuItem>
      </Menu>
    </>
  );
}
